import { Listing } from 'models/Listing';
import { ASKING_PRICE_SELECT } from 'models/DealRoom';
import { ListingFieldBaseName } from 'router/data/defaultRequired';
import utils, { getArrayLength, getHasElementsInArray } from 'utils';

/**
 * The single rule set describing what the public listing needs, read by both
 * form view and preview mode.
 *
 * A field is required only if it has an entry here. Listing images, the company
 * logo, stock at valuation and the precise map location are deliberately absent
 * - they are optional, so they are never marked and never counted.
 */
export const PUBLIC_LISTING_REQUIREMENT = {
  LISTING_TITLE: 'listingTitle',
  LOCATION: 'location',
  INDUSTRY: 'industry',
  PRICE_GUIDE: 'priceGuide',
  REVENUE: 'revenue',
  EBITDA: 'EBITDA',
  BUSINESS_DESCRIPTION: 'businessDescription',
  /** Highlights, Products and Key assets together - one outstanding item, not three. */
  LIST_SECTIONS: 'listSections',
} as const;

export type PublicListingRequirementKey =
  typeof PUBLIC_LISTING_REQUIREMENT[keyof typeof PUBLIC_LISTING_REQUIREMENT];

/**
 * Two of the three list sections must each hold this many entries. Also how
 * many rows of each section the form view keeps undeletable, so the protected
 * rows are exactly the ones a broker has to fill.
 */
export const MIN_REQUIRED_LIST_ITEMS = 2;

/** How many of the three sections must reach MIN_REQUIRED_LIST_ITEMS. */
export const MIN_SATISFIED_LIST_SECTIONS = 2;

export const LIST_SECTION_NAMES = [
  'highlights',
  'products',
  'keyAssets',
] as const;

/**
 * The form seeds every section with blank rows (Array(3) / Array(2) of
 * undefined) and a row the broker clears leaves an empty string behind, so
 * neither the raw length nor a plain undefined filter says how much is entered.
 */
export const getFilledListItems = (items?: Array<string | undefined>) =>
  (items || []).filter((item) => !!item?.trim());

export const getIsListSectionSatisfied = (items?: Array<string | undefined>) =>
  getArrayLength(getFilledListItems(items)) >= MIN_REQUIRED_LIST_ITEMS;

/** How many of Highlights / Products / Key assets hold two entries or more. */
export const getSatisfiedListSectionCount = (values: Partial<Listing>) =>
  getArrayLength(
    LIST_SECTION_NAMES.filter((name) =>
      getIsListSectionSatisfied(values[name]),
    ),
  );

export const getIsListSectionsRuleSatisfied = (values: Partial<Listing>) =>
  getSatisfiedListSectionCount(values) >= MIN_SATISFIED_LIST_SECTIONS;

/**
 * Where "Show missing fields" lands when the two-of-three rule is unmet: the
 * first section that does not yet hold its two entries. Falls back to the first
 * section, which the rule being unmet makes unreachable.
 */
export const getFirstUnsatisfiedListSection = (values: Partial<Listing>) =>
  LIST_SECTION_NAMES.find((name) => !getIsListSectionSatisfied(values[name])) ??
  LIST_SECTION_NAMES[0];

/**
 * Money reaches us as a dotted string in form state and as a number on the
 * listing, and an untouched field is '' rather than undefined.
 */
const hasMoneyValue = (value?: string | number | null) => {
  if (value === null || value === undefined || value === '') return false;

  const amount =
    typeof value === 'number'
      ? value
      : utils.getIntegerFromDottedNumber(String(value));

  return !!amount;
};

const hasTextValue = (value?: string) => !!value?.trim();

/**
 * Which `*RequiredVisibleInfo` key decides the price guide depends on the
 * asking-price type: a fixed price collects one amount, the other two collect a
 * range.
 *
 * An expression of interest publishes no figure, but still collects the range -
 * it is what the listing is searched and filtered on - and the form marks both
 * amounts required, so preview mode counts them the same way.
 */
const getPriceGuideRequiredInfoKeys = (
  values: Partial<Listing>,
): ListingFieldBaseName[] => {
  const { askingPriceSelect } = values;

  if (askingPriceSelect === ASKING_PRICE_SELECT.FIXED_PRICE) return ['amount'];

  return ['amountFrom', 'amountTo'];
};

/**
 * The amount the price guide's mark and "Show missing fields" land on, rather
 * than the type selector above them - the selector is answered, the amount
 * under it is not.
 */
const getPriceGuideFocusFieldName = (values: Partial<Listing>) =>
  values.askingPriceSelect === ASKING_PRICE_SELECT.FIXED_PRICE
    ? 'amount'
    : 'amountFrom';

export type PublicListingRequirement = {
  key: PublicListingRequirementKey;
  /**
   * The preview field this requirement marks and scrolls to. Resolved against
   * the listing type, since urgent listings collect a postcode where going
   * concerns collect a state, and against the current values, since the
   * list-section group spans three fields and lands on whichever is still short.
   */
  getFieldName?: (isUrgent: boolean, values: Partial<Listing>) => string;
  /**
   * The control inside that field to put the caret in, where the field opens as
   * a panel of several - the financial block. Focused and touched, so the input
   * shows its own required error in place rather than only once the block is
   * closed again. Omitted where the field is the control.
   */
  getFocusFieldName?: (values: Partial<Listing>) => string;
  /** Urgent listings collect a different set - see the entries below. */
  appliesTo?: (isUrgent: boolean) => boolean;
  /**
   * Carried over by the deal room creation step rather than entered here, so a
   * draft nobody has worked on still has it satisfied. Such a requirement
   * counts towards what is outstanding like any other, but never towards
   * whether the broker has entered anything yet.
   */
  isPrefilledFromDeal?: (isUrgent: boolean) => boolean;
  /**
   * Financial fields defer to the per-listing isRequired override rather than
   * being unconditionally required. Resolved against the current values because
   * the price guide's key depends on the asking-price type.
   */
  getRequiredInfoKeys?: (values: Partial<Listing>) => ListingFieldBaseName[];
  isSatisfied: (values: Partial<Listing>, isUrgent: boolean) => boolean;
  /**
   * Whether the broker has entered anything here at all, which is what the
   * bottom bar reads to tell an untouched draft from one being worked on.
   * Only the requirements met by a threshold rather than by a value being
   * present need their own answer - a single highlight, or one end of a price
   * range, is work done even though the rule is still unmet. Everything else
   * has nothing to enter short of satisfying it.
   */
  hasInput?: (values: Partial<Listing>, isUrgent: boolean) => boolean;
};

/**
 * Authored in the order the fields appear in preview mode, so "the first empty
 * required field" is simply the first outstanding entry.
 */
export const PUBLIC_LISTING_REQUIREMENTS: PublicListingRequirement[] = [
  {
    key: PUBLIC_LISTING_REQUIREMENT.LISTING_TITLE,
    getFieldName: () => 'listingTitle',
    isSatisfied: ({ listingTitle }) => hasTextValue(listingTitle),
  },
  {
    key: PUBLIC_LISTING_REQUIREMENT.LOCATION,
    getFieldName: (isUrgent) =>
      isUrgent ? 'locationPostcode' : 'dealLocation',
    /**
     * A going concern takes its deal location straight from the deal. An urgent
     * listing collects a postcode, which nothing fills in for the broker.
     */
    isPrefilledFromDeal: (isUrgent) => !isUrgent,
    /**
     * The published listing renders the precise state, then the postcode, then
     * the deal location, so a going concern with a deal location set already
     * shows a location and has nothing outstanding here.
     */
    isSatisfied: ({ locationPostcode, location, dealLocation }, isUrgent) =>
      isUrgent
        ? hasTextValue(locationPostcode)
        : hasTextValue(location?.state) || hasTextValue(dealLocation),
  },
  {
    key: PUBLIC_LISTING_REQUIREMENT.INDUSTRY,
    getFieldName: () => 'industry',
    /** The deal carries its industry across, and an unanswered one lands as Other. */
    isPrefilledFromDeal: () => true,
    isSatisfied: ({ industry }) => hasTextValue(industry),
  },
  {
    key: PUBLIC_LISTING_REQUIREMENT.PRICE_GUIDE,
    getFieldName: () => 'askingPriceSelect',
    getFocusFieldName: getPriceGuideFocusFieldName,
    // Urgent listings have no price guide in their Statement of Information.
    appliesTo: (isUrgent) => !isUrgent,
    getRequiredInfoKeys: getPriceGuideRequiredInfoKeys,
    isSatisfied: (values) => {
      const { askingPriceSelect, amount, amountFrom, amountTo } = values;

      if (!askingPriceSelect) return false;

      if (askingPriceSelect === ASKING_PRICE_SELECT.FIXED_PRICE)
        return hasMoneyValue(amount);

      return hasMoneyValue(amountFrom) && hasMoneyValue(amountTo);
    },
    /**
     * One end of a range is entered work. The type selector itself is not - it
     * arrives already set to a fixed price - unless it has been moved off that
     * to an expression of interest, which asks for no amount at all.
     */
    hasInput: ({ askingPriceSelect, amount, amountFrom, amountTo }) =>
      askingPriceSelect === ASKING_PRICE_SELECT.EXPRESSION_OF_INTEREST ||
      hasMoneyValue(amount) ||
      hasMoneyValue(amountFrom) ||
      hasMoneyValue(amountTo),
  },
  {
    key: PUBLIC_LISTING_REQUIREMENT.REVENUE,
    // Revenue has no preview field of its own - it is a row of the one block.
    getFieldName: () => 'askingPriceSelect',
    getFocusFieldName: () => 'revenue',
    getRequiredInfoKeys: () => ['revenue'],
    isSatisfied: ({ revenue }) => hasMoneyValue(revenue),
  },
  {
    key: PUBLIC_LISTING_REQUIREMENT.EBITDA,
    getFieldName: () => 'askingPriceSelect',
    getFocusFieldName: () => 'EBITDA',
    getRequiredInfoKeys: () => ['EBITDA'],
    isSatisfied: ({ EBITDA }) => hasMoneyValue(EBITDA),
  },
  {
    key: PUBLIC_LISTING_REQUIREMENT.BUSINESS_DESCRIPTION,
    getFieldName: () => 'businessDescription',
    isSatisfied: ({ businessDescription }) => hasTextValue(businessDescription),
  },
  {
    key: PUBLIC_LISTING_REQUIREMENT.LIST_SECTIONS,
    /**
     * The rule spans three sections and marks none of them, but "Show missing
     * fields" still has to land somewhere - the first section still short of
     * two entries, so a broker who has already filled Highlights is sent to
     * Products rather than back to work that is done.
     */
    getFieldName: (_isUrgent, values) => getFirstUnsatisfiedListSection(values),
    isSatisfied: getIsListSectionsRuleSatisfied,
    /** A single entry anywhere across the three sections is work done. */
    hasInput: (values) =>
      LIST_SECTION_NAMES.some((name) =>
        getHasElementsInArray(getFilledListItems(values[name])),
      ),
  },
];

/** The financial rows the preview tiles can mark, keyed by requirement. */
export const REQUIREMENT_PRICE_ROW = {
  [PUBLIC_LISTING_REQUIREMENT.PRICE_GUIDE]: 'askingPrice',
  [PUBLIC_LISTING_REQUIREMENT.REVENUE]: 'revenue',
  [PUBLIC_LISTING_REQUIREMENT.EBITDA]: 'EBITDA',
} as const;
